import * as React from 'react';

import { postCaption } from '@/api/aiTagging/postCaption';
import { useCurrent } from '@/hooks/useCurrent';

export type TagLabel = { _id: string; name: string };

export type AiTaggingResult = {
  selectedHashtags: TagLabel[];
  recommendedHashtags: string[];
};

const DEBOUNCE_MS = 2000;
const MIN_CONTENT_LENGTH = 5;

/**
 * Normalize text for the AI API: replace fancy Unicode quotes/dashes with ASCII,
 * collapse runs of whitespace (including newlines) to a single space.
 * Fixes cases where pasted content returns empty tags due to encoding/format.
 */
function normalizeCaption(text: string): string {
  return text
    .replace(/[\u201c\u201d\u201e\u201f\u2033\u2036"]/g, '"')
    .replace(/[\u2018\u2019\u201a\u2032]/g, "'")
    .replace(/\u2014/g, '--')
    .replace(/\u2013/g, '-')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Dedupe labels by name (case-insensitive), keep first id. Avoids duplicate names confusing the AI. */
function dedupeLabels(labels: TagLabel[]): TagLabel[] {
  const seen = new Set<string>();
  return labels.filter((l) => {
    const key = (l.name || '').trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

type UseAiTaggingArgs = {
  /** Current title (plain text) */
  title: string;
  /** Current body as plain text (e.g. from draft-js getCurrentContent().getPlainText()) */
  bodyPlainText: string;
  /** All available tags to send as labels to the AI (e.g. from tagsWithSearchTerm with high limit) */
  labels: TagLabel[];
  /** Called when AI returns; use this to set form tags to selected + merge with existing if desired */
  onResult: (result: AiTaggingResult) => void;
  /** If false, skip calling the API (e.g. when labels are not loaded yet) */
  enabled?: boolean;
};

/**
 * When user has entered title/body content, calls the AI tagging microservice and
 * passes back selected_hashtags (to show as selected tags) and recommended_hashtags
 * (to send to backend on submit).
 */
export function useAiTagging({
  title,
  bodyPlainText,
  labels,
  onResult,
  enabled = true,
}: UseAiTaggingArgs): {
  recommendedHashtags: string[];
  isLoading: boolean;
  error: Error | null;
  setRecommendedHashtags: React.Dispatch<React.SetStateAction<string[]>>;
} {
  const [recommendedHashtags, setRecommendedHashtags] = React.useState<
    string[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const onResultRef = React.useRef(onResult);
  onResultRef.current = onResult;

  const current = useCurrent();
  const tenantId = current?.tenant?._id;
  const aiTaggingDisabled = current?.tenant?.featureFlags?.aiTaggingDisabled ?? false;

  React.useEffect(() => {
    if (!enabled || aiTaggingDisabled) return;

    const bodyNormalized = normalizeCaption(bodyPlainText || '');
    if (bodyNormalized.length <= MIN_CONTENT_LENGTH) return;

    const titleNormalized = normalizeCaption(title || '');
    const labelsDeduped = dedupeLabels(labels);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      setIsLoading(true);
      setError(null);
      postCaption(
        {
          post_title: titleNormalized,
          post_caption: bodyNormalized,
          labels: labelsDeduped.map((l) => ({ id: l._id, name: l.name })),
        },
        tenantId
      )
        .then((res) => {
          const labelsById = new Map(labelsDeduped.map((l) => [l._id, l.name]));
          const labelsByName = new Map(
            labelsDeduped.map((l) => [normalizeCaption(l.name).toLowerCase(), l])
          );

          const selectedRaw: unknown = (res as any).selected_hashtags ?? [];

          const selectedHashtags = (Array.isArray(selectedRaw) ? selectedRaw : [])
            .map((h: any) => {
              // New shape: string[] of names
              if (typeof h === 'string') {
                const key = normalizeCaption(h).toLowerCase();
                const match = labelsByName.get(key);
                return match ? { _id: match._id, name: match.name } : null;
              }

              // Old shape: {id,name}[]
              const id = String(h?.id ?? '').trim();
              if (!id) return null;
              const name = normalizeCaption(
                String(h?.name ?? labelsById.get(id) ?? '')
              );
              if (!name) return null;
              return { _id: id, name };
            })
            .filter((t): t is TagLabel => !!t && !!t._id && !!t.name);

          const recommended = (res.recommended_hashtags || [])
            .map((s: string) => normalizeCaption(String(s ?? '')))
            .filter((s: string) => !!s);
          setRecommendedHashtags(recommended);
          onResultRef.current({
            selectedHashtags,
            recommendedHashtags: recommended,
          });
        })
        .catch((err) => {
          setError(err instanceof Error ? err : new Error(String(err)));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [title, bodyPlainText, labels, enabled, tenantId, aiTaggingDisabled]);

  return {
    recommendedHashtags,
    isLoading,
    error,
    setRecommendedHashtags,
  };
}
